/*
 * NJIELI – Schritt 2 im Warenkorb: Herzensprojekt wählen.
 * - speichert die Auswahl sofort als Cart-Attribut (NJIELI Projekt-ID / NJIELI Projekt)
 * - öffnet Projektdetails im <dialog>
 * - Pflichtauswahl: "Zur Kasse" führt ohne Auswahl zurück zu Schritt 2
 * Der Footer wird bei Mengenänderungen per innerHTML neu gerendert; der Zustand
 * wird deshalb in connectedCallback jedes Mal neu aufgebaut.
 */
(() => {
  if (customElements.get('njieli-project-picker')) return;

  const updateUrl = () => `${(window.routes && window.routes.cart_update_url) || '/cart/update'}.js`;

  // Überlebt Footer-Re-Renders.
  let pendingSelection = null;
  let requestSeq = 0;

  const getPicker = () => document.querySelector('njieli-project-picker');

  class NjieliProjectPicker extends HTMLElement {
    connectedCallback() {
      this.statusEl = this.querySelector('[data-njieli-status]');
      this.onChange = this.onChange.bind(this);
      this.onClick = this.onClick.bind(this);
      this.addEventListener('change', this.onChange);
      this.addEventListener('click', this.onClick);

      if (pendingSelection !== null) {
        const input = this.findInput(pendingSelection.id);
        if (input) input.checked = true;
      }

      if (this.hasAttribute('data-stale')) {
        this.removeAttribute('data-stale');
        this.save('', '', { silent: true });
      }

      this.ensureNameField();
      this.syncSummary();
    }

    disconnectedCallback() {
      this.removeEventListener('change', this.onChange);
      this.removeEventListener('click', this.onClick);
    }

    get radios() {
      return Array.from(this.querySelectorAll('.njieli-tile__radio'));
    }

    findInput(id) {
      return this.radios.find((el) => el.value === id);
    }

    get selectedInput() {
      return this.radios.find((el) => el.checked) || null;
    }

    hasSelection() {
      return Boolean(this.selectedInput && this.selectedInput.value);
    }

    // Name des Projekts zusätzlich als Formularfeld, damit er auch dann am
    // Warenkorb landet, wenn der Kunde sofort nach dem Klick zur Kasse geht.
    // Ebenso die interne Projekt-Referenz (Metaobjekt-GID) für die Auswertung in Shopify Flow.
    ensureNameField() {
      const selected = this.selectedInput;
      const fields = [
        [this.dataset.attrNameKey, 'projectName'],
        [this.dataset.attrRefKey, 'projectRef'],
      ];
      fields.forEach(([key, dataKey]) => {
        if (!key) return;
        let field = this.querySelector(`input[type="hidden"][name="attributes[${key}]"]`);
        if (!field) {
          field = document.createElement('input');
          field.type = 'hidden';
          field.name = `attributes[${key}]`;
          field.setAttribute('form', 'cart');
          this.appendChild(field);
        }
        field.value = selected ? selected.dataset[dataKey] || '' : '';
      });
    }

    onChange(event) {
      const input = event.target;
      if (!input.classList || !input.classList.contains('njieli-tile__radio')) return;
      event.stopPropagation();
      this.save(input.value, input.dataset.projectName || '');
    }

    onClick(event) {
      const opener = event.target.closest('[data-njieli-open]');
      if (opener) {
        const dialog = document.getElementById(opener.dataset.njieliOpen);
        if (dialog && typeof dialog.showModal === 'function') {
          dialog.showModal();
          document.documentElement.classList.add('njieli-dialog-open');
          dialog.addEventListener('close', () => document.documentElement.classList.remove('njieli-dialog-open'), { once: true });
        }
        return;
      }

      const closer = event.target.closest('[data-njieli-close]');
      if (closer) {
        closer.closest('dialog').close();
        return;
      }

      const chooser = event.target.closest('[data-njieli-choose]');
      if (chooser) {
        const input = this.findInput(chooser.dataset.njieliChoose);
        if (input && !input.checked) {
          input.checked = true;
          this.save(input.value, input.dataset.projectName || '');
        }
        const dialog = chooser.closest('dialog');
        setTimeout(() => {
          if (dialog && dialog.open) dialog.close();
          if (input) input.focus({ preventScroll: true });
        }, 350);
        return;
      }

      // Klick auf den Hintergrund (außerhalb des Inhalts) schließt das Pop-up.
      if (event.target.tagName === 'DIALOG') event.target.close();
    }

    async save(id, name, { silent = false } = {}) {
      const seq = ++requestSeq;
      pendingSelection = { id, name };
      this.ensureNameField();
      this.syncSummary();
      this.classList.remove('njieli-pick--attention');
      if (!silent) this.setStatus('Wird gespeichert …');

      const attributes = {};
      attributes[this.dataset.attrIdKey] = id;
      attributes[this.dataset.attrNameKey] = name;
      if (this.dataset.attrRefKey) {
        const input = id ? this.findInput(id) : null;
        attributes[this.dataset.attrRefKey] = input ? input.dataset.projectRef || '' : '';
      }

      try {
        const response = await fetch(updateUrl(), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ attributes }),
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        if (seq !== requestSeq) return;
        pendingSelection = null;
        this.dataset.selected = id;
        const stale = this.querySelector('[data-njieli-stale]');
        if (stale && id) stale.remove();
        if (!silent) this.setStatus(id ? `Danke! Deine Bestellung unterstützt „${name}“.` : '');
      } catch (error) {
        console.error('[NJIELI] Projektauswahl konnte nicht gespeichert werden', error);
        if (seq !== requestSeq) return;
        pendingSelection = null;
        const previous = this.findInput(this.dataset.selected || '');
        this.radios.forEach((el) => { el.checked = el === previous; });
        this.ensureNameField();
        this.syncSummary();
        this.setStatus('Die Auswahl konnte nicht gespeichert werden. Bitte versuche es erneut.', true);
      }
    }

    // Bestellübersicht (Schritt 3) an den Auswahlzustand anpassen.
    syncSummary() {
      const selected = this.hasSelection() ? this.selectedInput : null;
      const recap = document.querySelector('[data-njieli-recap]');
      if (recap) {
        const nameEl = recap.querySelector('[data-njieli-recap-name]');
        recap.classList.toggle('is-selected', Boolean(selected));
        if (nameEl) nameEl.textContent = selected ? selected.dataset.projectName || '' : '';
      }
      document.querySelectorAll('.js-cart-checkout-button').forEach((button) => {
        button.classList.toggle('njieli-checkout--locked', !selected);
        if (selected) button.removeAttribute('aria-describedby');
        else button.setAttribute('aria-describedby', 'NjieliCheckoutHint');
      });
    }

    requireSelection() {
      this.setStatus('Bitte wähle zuerst ein Herzensprojekt aus.', true);
      this.classList.remove('njieli-pick--attention');
      // Reflow, damit die Animation erneut startet.
      void this.offsetWidth;
      this.classList.add('njieli-pick--attention');
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
      const first = this.radios[0];
      if (first) first.focus({ preventScroll: true });
    }

    setStatus(message, isError = false) {
      if (!this.statusEl) return;
      this.statusEl.textContent = message;
      this.statusEl.classList.toggle('njieli-pick__status--error', isError);
    }
  }

  customElements.define('njieli-project-picker', NjieliProjectPicker);

  // Pflichtauswahl: Checkout abfangen, solange kein Projekt gewählt ist.
  const blockIfMissing = (event) => {
    const picker = getPicker();
    if (!picker || picker.hasSelection()) return false;
    event.preventDefault();
    event.stopImmediatePropagation();
    picker.requireSelection();
    return true;
  };

  document.addEventListener(
    'click',
    (event) => {
      const button = event.target.closest && event.target.closest('.js-cart-checkout-button, [name="checkout"]');
      if (button) blockIfMissing(event);
    },
    true
  );

  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!form || form.id !== 'cart') return;
      const submitter = event.submitter;
      if (submitter && submitter.name !== 'checkout') return;
      blockIfMissing(event);
    },
    true
  );
})();
